import React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CenterScreen from "../../../components/wrappers/CenterScreen";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { LOGIN, USER_ACTIVATION } from "../../../enums/UrlPaths";
import { USER_ACTIVATION_CONFIRM } from "../../../enums/ApiPaths";
import authApi from "../../../utils/api/authApi";
import {
  getBearerToken,
  getTokenFromUrl,
  logout,
} from "../../../utils/Functions";
import { useLanguageInUrl } from "./useLanguageInUrl";

const ELSE_WIDTH = "40%";
const REDIRECT_SECONDS = 15;

/**
 * Opened from the confirmation mail sent by UserActivationPage - activates the account
 * with the password chosen there, then sends the user on to the login page.
 */
export default function UserActivationConfirmPage() {
  const { t } = useTranslation();
  const urlLanguage = useLanguageInUrl();
  const navigate = useNavigate();
  const token = getTokenFromUrl();

  // A query rather than an effect so StrictMode's double mount can't send the one-time
  // token twice (the second call would fail and replace the success message)
  const confirmation = useQuery({
    queryKey: ["userActivationConfirm", token],
    queryFn: async () => {
      const res = await authApi.post(USER_ACTIVATION_CONFIRM + token);
      // Someone else logged in on this browser must not stay logged in over this account
      if (await getBearerToken()) {
        logout();
      }
      return res.data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const [secondsLeft, setSecondsLeft] = React.useState(REDIRECT_SECONDS);
  React.useEffect(() => {
    if (!confirmation.isSuccess) return;
    if (secondsLeft <= 0) {
      navigate(LOGIN);
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [confirmation.isSuccess, secondsLeft, navigate]);

  return (
    <CenterScreen>
      <Stack
        sx={{
          width: {
            xs: "90%",
            sm: "90%",
            md: ELSE_WIDTH,
            lg: ELSE_WIDTH,
            xl: ELSE_WIDTH,
          },
        }}
        spacing={2}
      >
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h4">{t("UserActivation")}</Typography>
            <LanguageSwitcher
              defaultLanguage={urlLanguage}
              testIdPrefix="user-activation-confirm.language"
            />
            {confirmation.isLoading ? (
              <Stack alignItems="center" py={2} spacing={1}>
                <CircularProgress />
                <Typography>{t("UserActivationConfirming")}</Typography>
              </Stack>
            ) : confirmation.isSuccess ? (
              <>
                <Alert
                  severity="success"
                  data-testid="user-activation-confirm.success"
                >
                  {t("UserActivationConfirmed")}
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  {t("RedirectingToLogin", { seconds: secondsLeft })}
                </Typography>
                <Button
                  variant="contained"
                  color="info"
                  onClick={() => navigate(LOGIN)}
                  data-testid="user-activation-confirm.login-button"
                >
                  {t("GoToLogin")}
                </Button>
              </>
            ) : (
              <>
                <Alert
                  severity="error"
                  data-testid="user-activation-confirm.invalid-link"
                >
                  {t("UserActivationLinkInvalid")}
                </Alert>
                <Button
                  variant="outlined"
                  onClick={() => navigate(USER_ACTIVATION)}
                  data-testid="user-activation-confirm.retry-button"
                >
                  {t("UserActivationRequestNew")}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
    </CenterScreen>
  );
}
